import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Lang, normalizeLang, pickI18nText } from '@/utils/approval/i18nText';
import type { RelatedApprovalItem } from '@/types/approval';
import type { ApprovalViewRelatedItem } from '@/components/Approval/ApprovalView/types';
import useApprovalView from './useApprovalView';

/**
 * 조회 화면 연관품의(parents/children/related) + 북마크.
 * v2 단건조회(`useApprovalView`) 응답에서 파생 — v1 portal-data 단건조회를 조회 화면 한정으로 대체.
 *
 * 같은 SWR key 라 `useApprovalView` 재호출도 dedup — 단건조회는 한 번만 나간다.
 *
 * 쓰기(관계 추가/삭제)는 v1 `useRelatedApprovalMutations` 유지 — 쓰기 직후 `mutate` 로 v2 재검증.
 */
const EMPTY: RelatedApprovalItem[] = [];

const toItem = (
  raw: ApprovalViewRelatedItem,
  lang: Lang,
): RelatedApprovalItem => ({
  docId: raw.docId,
  subject: pickI18nText(raw.subject, lang),
  status: raw.status,
  author: { name: pickI18nText(raw.author?.name, lang) },
  authoredAt: raw.authoredAt,
});

const mapItems = (
  items: ApprovalViewRelatedItem[] | undefined,
  lang: Lang,
): RelatedApprovalItem[] =>
  items && items.length > 0 ? items.map((it) => toItem(it, lang)) : EMPTY;

const useApprovalViewRelations = (documentId?: string | null) => {
  const { i18n } = useTranslation();
  const lang = useMemo(() => normalizeLang(i18n.language), [i18n.language]);
  const { data, isLoading, mutate } = useApprovalView(documentId ?? null);

  const parents = useMemo(
    () => mapItems(data?.parents, lang),
    [data?.parents, lang],
  );
  const children = useMemo(
    () => mapItems(data?.children, lang),
    [data?.children, lang],
  );
  const related = useMemo(
    () => mapItems(data?.related, lang),
    [data?.related, lang],
  );

  return {
    parents,
    children,
    related,
    isBookmark: data?.isBookmark ?? false,
    isLoading,
    mutate,
  };
};

export default useApprovalViewRelations;
